/**
 * BTI Chat Assistant - Main Application
 */

import { validateConfig, APP_CONFIG } from './config.js';
import { queryVectara, createChatTurn, resetChatSession, VectaraAPIError } from './vectara-api.js';

// DOM Elements
let elements = {};

// Suggested questions for BTI coaching
const SUGGESTED_QUESTIONS = [
  "How do I set a weekly plan that increases time freedom without burning out?",
  "How do I structure my first dream?",
  "How do I write a clear, compelling vision statement?",
  "What are the key principles of Brave Thinking?",
  "How can I overcome limiting beliefs?",
  "What is the difference between a dream and a goal?"
];

// Application State
const state = {
  messages: [],
  isLoading: false,
  lastRequestTime: 0,
  currentConversationId: null,
  conversations: new Map(),
  sidebarCollapsed: false,
  sidebarOpen: false, // For mobile
};

/**
 * Initialize the application
 */
function init() {
  // Validate configuration
  if (!validateConfig()) {
    showConfigError();
    return;
  }

  // Reset chat session for fresh start
  resetChatSession();

  // Cache DOM elements
  cacheElements();

  // Set up event listeners
  setupEventListeners();

  console.log('BTI Chat Assistant initialized');

  // Load conversations from localStorage
  loadConversations();
  renderConversationsList();

  // Load sidebar preferences
  loadSidebarPreferences();

  // Show suggestions initially
  showSuggestions();
}

/**
 * Cache DOM elements for better performance
 */
function cacheElements() {
  elements = {
    messagesContainer: document.getElementById('messagesContainer'),
    typingIndicator: document.getElementById('typingIndicator'),
    chatForm: document.getElementById('chatForm'),
    chatInput: document.getElementById('chatInput'),
    sendButton: document.getElementById('sendButton'),
    charCounter: document.getElementById('charCounter'),
    suggestionsContainer: document.getElementById('suggestionsContainer'),
    newChatButton: document.getElementById('newChatButton'),
    chatSidebar: document.getElementById('chatSidebar'),
    conversationsList: document.getElementById('conversationsList'),
    sidebarToggle: document.getElementById('sidebarToggle'),
    hamburgerMenu: document.getElementById('hamburgerMenu'),
    sidebarOverlay: document.getElementById('sidebarOverlay'),
  };
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
  // Form submission
  elements.chatForm.addEventListener('submit', handleFormSubmit);

  // Input changes
  elements.chatInput.addEventListener('input', handleInputChange);

  // Enter key handling (without shift)
  elements.chatInput.addEventListener('keydown', handleKeyDown);

  // New chat button
  elements.newChatButton.addEventListener('click', startNewConversation);

  // Sidebar controls
  elements.sidebarToggle.addEventListener('click', toggleSidebar);
  elements.hamburgerMenu.addEventListener('click', openMobileSidebar);
  elements.sidebarOverlay.addEventListener('click', closeMobileSidebar);
}

/**
 * Handle form submission
 */
async function handleFormSubmit(e) {
  e.preventDefault();

  const message = elements.chatInput.value.trim();
  if (!message || state.isLoading) return;

  // Check debounce
  const now = Date.now();
  if (now - state.lastRequestTime < APP_CONFIG.DEBOUNCE_DELAY) {
    showToast('Please wait a moment before sending another message');
    return;
  }

  state.lastRequestTime = now;

  // Add user message
  addMessage(message, 'user');

  // Clear input
  elements.chatInput.value = '';
  updateCharCounter();

  // Hide suggestion buttons after first message
  hideSuggestions();

  // Show typing indicator
  showTypingIndicator();

  // Disable input during processing
  setInputState(false);

  try {
    // Use queryVectara for all messages since v1 API doesn't have chat sessions
    const response = await queryVectara(message);

    hideTypingIndicator();

    // Handle structured response format
    if (response && typeof response === 'object' && response.text) {
      // Structured response with text, sources, and followUpQuestions
      addMessage(response.text, 'assistant', '', response.sources || [], response.followUpQuestions || []);
    } else if (typeof response === 'string') {
      // String response (usually error messages from handleApiError)
      addMessage(response, 'assistant');
    } else {
      // Fallback for unexpected response format
      addMessage(response || 'No response received', 'assistant');
    }
  } catch (error) {
    hideTypingIndicator();

    // Simple error handling - the API functions now return error strings directly
    let errorMessage = 'I apologize, but I encountered an error. Please try again.';

    if (typeof error === 'string') {
      errorMessage = error;
    } else if (!navigator.onLine) {
      errorMessage = 'You appear to be offline. Please check your connection and try again.';
    }

    addMessage(errorMessage, 'assistant', 'error');
    console.error('Error:', error);
  } finally {
    setInputState(true);
  }
}

/**
 * Handle input changes
 */
function handleInputChange(e) {
  updateCharCounter();

  // Auto-resize textarea
  const textarea = e.target;
  textarea.style.height = 'auto';
  textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';

  // Enable/disable send button
  const hasContent = textarea.value.trim().length > 0;
  elements.sendButton.disabled = !hasContent || state.isLoading;
}

/**
 * Handle keyboard events
 */
function handleKeyDown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    elements.chatForm.requestSubmit();
  }
}

/**
 * Update character counter
 */
function updateCharCounter() {
  const length = elements.chatInput.value.length;
  const maxLength = APP_CONFIG.MAX_INPUT_LENGTH;

  elements.charCounter.textContent = `${length}/${maxLength}`;

  if (length > maxLength * 0.9) {
    elements.charCounter.classList.add('warning');
  } else {
    elements.charCounter.classList.remove('warning');
  }
}

/**
 * Add a message to the chat
 */
function addMessage(content, sender, type = '', sources = [], followUpQuestions = []) {
  const messageDiv = document.createElement('div');
  messageDiv.className = `message ${sender}-message ${type}`;

  const avatarDiv = document.createElement('div');
  avatarDiv.className = 'message-avatar';

  if (sender === 'user') {
    const avatarText = document.createElement('span');
    avatarText.className = 'avatar-text';
    avatarText.textContent = 'You';
    avatarDiv.appendChild(avatarText);
  } else {
    const avatarImg = document.createElement('img');
    avatarImg.src = '/images/mary-morrissey.jpg';
    avatarImg.alt = 'Mary Morrissey';
    avatarImg.className = 'avatar-image';
    avatarDiv.appendChild(avatarImg);
  }

  const contentDiv = document.createElement('div');
  contentDiv.className = 'message-content';

  const contentP = document.createElement('p');
  contentP.textContent = content;
  contentDiv.appendChild(contentP);

  // Add sources if available
  if (sources && sources.length > 0) {
    const sourcesDiv = document.createElement('div');
    sourcesDiv.className = 'message-sources';

    const sourcesTitle = document.createElement('h4');
    sourcesTitle.className = 'sources-title';
    sourcesTitle.textContent = 'Sources:';
    sourcesDiv.appendChild(sourcesTitle);

    const sourcesList = document.createElement('div');
    sourcesList.className = 'sources-list';

    sources.forEach((source, index) => {
      const sourceItem = document.createElement('div');
      sourceItem.className = 'source-item';

      const sourceButton = document.createElement('button');
      sourceButton.className = 'source-button';
      sourceButton.textContent = `[${source.id}] ${source.title}`;
      sourceButton.title = source.text.substring(0, 200) + '...';

      sourceButton.addEventListener('click', () => {
        showSourceModal(source);
      });

      sourceItem.appendChild(sourceButton);
      sourcesList.appendChild(sourceItem);
    });

    sourcesDiv.appendChild(sourcesList);
    contentDiv.appendChild(sourcesDiv);
  }

  // Add follow-up questions if available
  if (followUpQuestions && followUpQuestions.length > 0) {
    const followUpDiv = document.createElement('div');
    followUpDiv.className = 'message-followup';

    const followUpTitle = document.createElement('h4');
    followUpTitle.className = 'followup-title';
    followUpTitle.textContent = 'Continue exploring:';
    followUpDiv.appendChild(followUpTitle);

    const followUpList = document.createElement('div');
    followUpList.className = 'followup-list';

    followUpQuestions.forEach((question, index) => {
      const questionButton = document.createElement('button');
      questionButton.className = 'followup-button';
      questionButton.textContent = question;

      questionButton.addEventListener('click', () => {
        // Auto-ask the follow-up question
        elements.chatInput.value = question;
        updateCharCounter();
        elements.chatForm.requestSubmit();
      });

      followUpList.appendChild(questionButton);
    });

    followUpDiv.appendChild(followUpList);
    contentDiv.appendChild(followUpDiv);
  }

  messageDiv.appendChild(avatarDiv);
  messageDiv.appendChild(contentDiv);

  elements.messagesContainer.appendChild(messageDiv);

  // Store in state
  state.messages.push({ content, sender, timestamp: Date.now(), sources, followUpQuestions });

  // Auto-save conversation after each message
  setTimeout(() => {
    saveCurrentConversation();
  }, 100);

  // Auto scroll to bottom
  setTimeout(() => scrollToBottom(), APP_CONFIG.AUTO_SCROLL_DELAY);
}

/**
 * Show source modal with full source text
 */
function showSourceModal(source) {
  // Create modal overlay
  const modalOverlay = document.createElement('div');
  modalOverlay.className = 'modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'source-modal';

  const modalHeader = document.createElement('div');
  modalHeader.className = 'modal-header';

  const modalTitle = document.createElement('h3');
  modalTitle.textContent = source.title;
  modalHeader.appendChild(modalTitle);

  const closeButton = document.createElement('button');
  closeButton.className = 'modal-close-button';
  closeButton.innerHTML = '×';
  closeButton.addEventListener('click', () => {
    modalOverlay.remove();
  });
  modalHeader.appendChild(closeButton);

  const modalContent = document.createElement('div');
  modalContent.className = 'modal-content';

  const sourceText = document.createElement('p');
  sourceText.textContent = source.text;
  modalContent.appendChild(sourceText);

  if (source.url) {
    const sourceLink = document.createElement('a');
    sourceLink.href = source.url;
    sourceLink.target = '_blank';
    sourceLink.rel = 'noopener noreferrer';
    sourceLink.textContent = 'View Original Source';
    sourceLink.className = 'source-link';
    modalContent.appendChild(sourceLink);
  }

  modal.appendChild(modalHeader);
  modal.appendChild(modalContent);
  modalOverlay.appendChild(modal);

  // Close on overlay click
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      modalOverlay.remove();
    }
  });

  document.body.appendChild(modalOverlay);
}

/**
 * Show typing indicator
 */
function showTypingIndicator() {
  state.isLoading = true;
  elements.typingIndicator.style.display = 'flex';
  setTimeout(() => scrollToBottom(), APP_CONFIG.AUTO_SCROLL_DELAY);
}

/**
 * Hide typing indicator
 */
function hideTypingIndicator() {
  state.isLoading = false;
  elements.typingIndicator.style.display = 'none';
}

/**
 * Set input state (enabled/disabled)
 */
function setInputState(enabled) {
  elements.chatInput.disabled = !enabled;
  elements.sendButton.disabled = !enabled || elements.chatInput.value.trim().length === 0;

  if (enabled) {
    elements.chatInput.focus();
  }
}

/**
 * Scroll to bottom of messages
 */
function scrollToBottom() {
  elements.messagesContainer.scrollTop = elements.messagesContainer.scrollHeight;
}

/**
 * Show toast notification
 */
function showToast(message, type = 'info') {
  // Simple toast implementation - can be enhanced with a toast library
  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--bti-gray-800);
    color: white;
    padding: 12px 24px;
    border-radius: 24px;
    z-index: 1000;
    animation: fadeInUp 0.3s ease-out;
  `;

  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.animation = 'fadeOutDown 0.3s ease-out';
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

/**
 * Show configuration error
 */
function showConfigError() {
  const errorMessage = `
    <div style="padding: 20px; text-align: center; color: var(--bti-error);">
      <h2>Configuration Required</h2>
      <p>Please configure your Vectara API credentials in the .env file.</p>
      <p>Check the console for specific missing variables.</p>
    </div>
  `;

  if (elements.messagesContainer) {
    elements.messagesContainer.innerHTML = errorMessage;
  }
}


// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

/**
 * Show suggestion buttons
 */
function showSuggestions() {
  if (!elements.suggestionsContainer) return;

  elements.suggestionsContainer.innerHTML = '';
  elements.suggestionsContainer.style.display = 'block';

  const suggestionsTitle = document.createElement('div');
  suggestionsTitle.className = 'suggestions-title';
  suggestionsTitle.textContent = 'Try asking:';
  elements.suggestionsContainer.appendChild(suggestionsTitle);

  const suggestionsGrid = document.createElement('div');
  suggestionsGrid.className = 'suggestions-grid';

  SUGGESTED_QUESTIONS.forEach(question => {
    const button = document.createElement('button');
    button.className = 'suggestion-button';
    button.textContent = question;
    button.addEventListener('click', () => {
      elements.chatInput.value = question;
      updateCharCounter();
      elements.chatForm.requestSubmit();
    });
    suggestionsGrid.appendChild(button);
  });

  elements.suggestionsContainer.appendChild(suggestionsGrid);
}

/**
 * Hide suggestion buttons
 */
function hideSuggestions() {
  if (elements.suggestionsContainer) {
    elements.suggestionsContainer.style.display = 'none';
  }
}

/**
 * Start a new conversation
 */
function startNewConversation() {
  // Save current conversation if it has messages
  saveCurrentConversation();

  // Clear messages except welcome message
  const welcomeMessage = elements.messagesContainer.querySelector('.welcome-message');
  elements.messagesContainer.innerHTML = '';

  if (welcomeMessage) {
    elements.messagesContainer.appendChild(welcomeMessage.cloneNode(true));
  }

  // Clear state
  state.messages = [];
  state.currentConversationId = null;

  // Reset chat session
  resetChatSession();

  // Show suggestions again
  showSuggestions();

  // Clear and focus input
  elements.chatInput.value = '';
  updateCharCounter();
  elements.chatInput.focus();

  // Update conversations list
  renderConversationsList();

  // Show success feedback
  showToast('New conversation started!', 'success');
}

/**
 * Conversation History Management
 */

// Load conversations from localStorage
function loadConversations() {
  try {
    const saved = localStorage.getItem('bti_conversations');
    if (saved) {
      const conversationsArray = JSON.parse(saved);
      state.conversations.clear();
      conversationsArray.forEach(conv => {
        state.conversations.set(conv.id, conv);
      });
    }
  } catch (error) {
    console.error('Error loading conversations:', error);
    state.conversations.clear();
  }
}

// Save conversations to localStorage
function saveConversations() {
  try {
    const conversationsArray = Array.from(state.conversations.values());
    localStorage.setItem('bti_conversations', JSON.stringify(conversationsArray));
  } catch (error) {
    console.error('Error saving conversations:', error);
  }
}

// Generate conversation ID
function generateConversationId() {
  return 'conv_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
}

// Create new conversation
function createConversation(title) {
  const id = generateConversationId();
  const conversation = {
    id,
    title: title || 'New Conversation',
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  state.conversations.set(id, conversation);
  state.currentConversationId = id;
  saveConversations();
  renderConversationsList();

  return conversation;
}

// Save current conversation
function saveCurrentConversation() {
  if (!state.currentConversationId && state.messages.length > 0) {
    // Create new conversation with first user message as title
    const firstUserMessage = state.messages.find(m => m.sender === 'user');
    const title = firstUserMessage ?
      firstUserMessage.content.substring(0, 50) + (firstUserMessage.content.length > 50 ? '...' : '') :
      'New Conversation';
    createConversation(title);
  }

  if (state.currentConversationId && state.conversations.has(state.currentConversationId)) {
    const conversation = state.conversations.get(state.currentConversationId);
    conversation.messages = [...state.messages];
    conversation.updatedAt = new Date().toISOString();

    // Update title if it's still "New Conversation"
    if (conversation.title === 'New Conversation' && state.messages.length > 0) {
      const firstUserMessage = state.messages.find(m => m.sender === 'user');
      if (firstUserMessage) {
        conversation.title = firstUserMessage.content.substring(0, 50) +
          (firstUserMessage.content.length > 50 ? '...' : '');
      }
    }

    saveConversations();
    renderConversationsList();
  }
}

// Load conversation
function loadConversation(conversationId) {
  if (!state.conversations.has(conversationId)) return;

  // Save current conversation first
  saveCurrentConversation();

  const conversation = state.conversations.get(conversationId);
  state.currentConversationId = conversationId;
  state.messages = [...conversation.messages];

  // Clear and rebuild messages container
  const welcomeMessage = elements.messagesContainer.querySelector('.welcome-message');
  elements.messagesContainer.innerHTML = '';

  if (welcomeMessage && state.messages.length === 0) {
    elements.messagesContainer.appendChild(welcomeMessage.cloneNode(true));
  }

  // Render all messages
  state.messages.forEach(message => {
    addMessageToDOM(message.content, message.sender, '', message.sources || [], message.followUpQuestions || []);
  });

  // Hide suggestions if there are messages
  if (state.messages.length > 0) {
    hideSuggestions();
  } else {
    showSuggestions();
  }

  renderConversationsList();
}

// Delete conversation
function deleteConversation(conversationId, event) {
  if (event) {
    event.stopPropagation();
  }

  if (!confirm('Delete this conversation?')) return;

  state.conversations.delete(conversationId);

  if (state.currentConversationId === conversationId) {
    // Start a new conversation
    startNewConversation();
  }

  saveConversations();
  renderConversationsList();
}

// Render conversations list
function renderConversationsList() {
  if (!elements.conversationsList) return;

  elements.conversationsList.innerHTML = '';

  const conversations = Array.from(state.conversations.values())
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  if (conversations.length === 0) {
    const emptyState = document.createElement('div');
    emptyState.className = 'empty-conversations';
    emptyState.innerHTML = `
      <p style="text-align: center; color: var(--text-secondary); padding: var(--spacing-lg);">
        No conversations yet.<br>Start chatting to see your history here!
      </p>
    `;
    elements.conversationsList.appendChild(emptyState);
    return;
  }

  conversations.forEach(conversation => {
    const item = document.createElement('div');
    item.className = `conversation-item ${conversation.id === state.currentConversationId ? 'active' : ''}`;

    const preview = conversation.messages.length > 0 ?
      (typeof conversation.messages[conversation.messages.length - 1].content === 'string'
        ? conversation.messages[conversation.messages.length - 1].content.substring(0, 60) + '...'
        : 'Recent message') :
      'No messages';

    const date = new Date(conversation.updatedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });

    item.innerHTML = `
      <div class="conversation-title">${conversation.title}</div>
      <div class="conversation-preview">${preview}</div>
      <div class="conversation-date">${date}</div>
      <button class="conversation-delete" onclick="deleteConversation('${conversation.id}', event)">×</button>
    `;

    item.addEventListener('click', () => loadConversation(conversation.id));
    elements.conversationsList.appendChild(item);
  });
}


// Add message to DOM without affecting state
function addMessageToDOM(content, sender, type = '', sources = [], followUpQuestions = []) {
  const messageDiv = document.createElement('div');
  messageDiv.className = `message ${sender}-message ${type}`;

  const avatarDiv = document.createElement('div');
  avatarDiv.className = 'message-avatar';

  if (sender === 'user') {
    const avatarText = document.createElement('span');
    avatarText.className = 'avatar-text';
    avatarText.textContent = 'You';
    avatarDiv.appendChild(avatarText);
  } else {
    const avatarImg = document.createElement('img');
    avatarImg.src = '/images/mary-morrissey.jpg';
    avatarImg.alt = 'Mary Morrissey';
    avatarImg.className = 'avatar-image';
    avatarDiv.appendChild(avatarImg);
  }

  const contentDiv = document.createElement('div');
  contentDiv.className = 'message-content';

  const contentP = document.createElement('p');
  contentP.textContent = content;
  contentDiv.appendChild(contentP);

  // Add sources if available
  if (sources && sources.length > 0) {
    const sourcesDiv = document.createElement('div');
    sourcesDiv.className = 'message-sources';

    const sourcesTitle = document.createElement('h4');
    sourcesTitle.className = 'sources-title';
    sourcesTitle.textContent = 'Sources:';
    sourcesDiv.appendChild(sourcesTitle);

    const sourcesList = document.createElement('div');
    sourcesList.className = 'sources-list';

    sources.forEach((source, index) => {
      const sourceItem = document.createElement('div');
      sourceItem.className = 'source-item';

      const sourceButton = document.createElement('button');
      sourceButton.className = 'source-button';
      sourceButton.textContent = `[${source.id}] ${source.title}`;
      sourceButton.title = source.text.substring(0, 200) + '...';

      sourceButton.addEventListener('click', () => {
        showSourceModal(source);
      });

      sourceItem.appendChild(sourceButton);
      sourcesList.appendChild(sourceItem);
    });

    sourcesDiv.appendChild(sourcesList);
    contentDiv.appendChild(sourcesDiv);
  }

  // Add follow-up questions if available
  if (followUpQuestions && followUpQuestions.length > 0) {
    const followUpDiv = document.createElement('div');
    followUpDiv.className = 'message-followup';

    const followUpTitle = document.createElement('h4');
    followUpTitle.className = 'followup-title';
    followUpTitle.textContent = 'Continue exploring:';
    followUpDiv.appendChild(followUpTitle);

    const followUpList = document.createElement('div');
    followUpList.className = 'followup-list';

    followUpQuestions.forEach((question, index) => {
      const questionButton = document.createElement('button');
      questionButton.className = 'followup-button';
      questionButton.textContent = question;

      questionButton.addEventListener('click', () => {
        // Auto-ask the follow-up question
        elements.chatInput.value = question;
        updateCharCounter();
        elements.chatForm.requestSubmit();
      });

      followUpList.appendChild(questionButton);
    });

    followUpDiv.appendChild(followUpList);
    contentDiv.appendChild(followUpDiv);
  }

  messageDiv.appendChild(avatarDiv);
  messageDiv.appendChild(contentDiv);
  elements.messagesContainer.appendChild(messageDiv);

  setTimeout(() => scrollToBottom(), APP_CONFIG.AUTO_SCROLL_DELAY);
}

/**
 * Sidebar Controls
 */

// Toggle sidebar (desktop)
function toggleSidebar() {
  state.sidebarCollapsed = !state.sidebarCollapsed;

  if (state.sidebarCollapsed) {
    elements.chatSidebar.classList.add('collapsed');
    // Change arrow direction
    const arrow = elements.sidebarToggle.querySelector('svg path');
    arrow.setAttribute('d', 'M9 18L15 12L9 6');
  } else {
    elements.chatSidebar.classList.remove('collapsed');
    // Change arrow direction
    const arrow = elements.sidebarToggle.querySelector('svg path');
    arrow.setAttribute('d', 'M15 18L9 12L15 6');
  }

  // Save preference
  localStorage.setItem('bti_sidebar_collapsed', state.sidebarCollapsed.toString());
}

// Open mobile sidebar
function openMobileSidebar() {
  state.sidebarOpen = true;
  elements.chatSidebar.classList.add('open');
  elements.sidebarOverlay.classList.add('open');
  renderConversationsList();
}

// Close mobile sidebar
function closeMobileSidebar() {
  state.sidebarOpen = false;
  elements.chatSidebar.classList.remove('open');
  elements.sidebarOverlay.classList.remove('open');
}

// Load sidebar preferences
function loadSidebarPreferences() {
  const collapsed = localStorage.getItem('bti_sidebar_collapsed');
  if (collapsed === 'true') {
    state.sidebarCollapsed = true;
    elements.chatSidebar.classList.add('collapsed');
    const arrow = elements.sidebarToggle.querySelector('svg path');
    arrow.setAttribute('d', 'M9 18L15 12L9 6');
  }
}

// Check if we're on mobile
function isMobile() {
  return window.innerWidth <= 768;
}

// Handle window resize
function handleResize() {
  if (!isMobile() && state.sidebarOpen) {
    closeMobileSidebar();
  }
}

// Add resize listener
window.addEventListener('resize', handleResize);

// Make deleteConversation globally available
window.deleteConversation = deleteConversation;

// Debug function to clear localStorage
window.clearStorage = function() {
  localStorage.clear();
  location.reload();
};

// Export for use in other modules
export { state, addMessage, showTypingIndicator, hideTypingIndicator };